import { ThemeSuggestionFragmentFragment } from "src/api/graphql/__generated__/graphql";
import { parsePalette } from "src/components/FloatingThemeMenu/helpers/parsePalette";
import parseStickerTemplateUrl from "src/helpers/parseV0TemplateUrl/parseStickerTemplateUrl";
import isPaletteMatch from "src/helpers/themeHelpers/isPaletteMatch";
import { EMOJI_REACTIONS } from "src/screens/(internal)/CustomThemeModal/constants";
import { ThemeCategoryOption } from "src/screens/(internal)/CustomThemeModal/types";

const parseOptionsFromThemeSuggestions = ({
	suggestions,
}: {
	suggestions: ThemeSuggestionFragmentFragment[];
}): {
	templateOptions: ThemeCategoryOption[];
	stickerOptions: ThemeCategoryOption[];
	paletteOptions: ThemeCategoryOption[];
	reactionOptions: ThemeCategoryOption[];
} => {
	if (!suggestions) {
		return {
			templateOptions: [],
			stickerOptions: [],
			paletteOptions: [],
			reactionOptions: [],
		};
	}

	// Template options must have at least a palette and sticker pack with thumbnail
	const templateOptions: ThemeCategoryOption[] = suggestions
		.map(({ palette: maybePalette, stickerPack, reaction, presetId }) => {
			const palette = parsePalette(maybePalette);
			if (presetId && palette && stickerPack?.stickerThumbnail?.readUrl) {
				const thumbnailUrl = parseStickerTemplateUrl({
					readUrl: stickerPack.stickerThumbnail.readUrl,
				});
				return {
					key: presetId,
					shape: "circle",
					optionItem: {
						palette,
						stickerPack: {
							presetId,
							stickerPackId: stickerPack.stickerPackId,
							thumbnailUrl,
						},
						reaction: reaction?.emoji ?? undefined,
					},
				} satisfies ThemeCategoryOption;
			}
			return null;
		})
		.filter(Boolean) as ThemeCategoryOption[];

	const paletteOptions: ThemeCategoryOption[] = [];
	for (const suggestion of suggestions) {
		const palette = parsePalette(suggestion.palette);
		if (
			palette &&
			!paletteOptions.find(({ optionItem: { palette: paletteOption } }) =>
				isPaletteMatch(paletteOption, palette),
			)
		) {
			paletteOptions.push({
				key: `${palette.primary}-${palette.secondary}-${palette.tertiary}`,
				shape: "square",
				optionItem: { palette },
			} satisfies ThemeCategoryOption);
		}
	}

	const stickerOptions = suggestions
		.map(({ stickerPack, presetId }) => {
			const { stickerThumbnail, stickerPackId } = stickerPack ?? {};
			const thumbnailUrl = parseStickerTemplateUrl({
				readUrl: stickerThumbnail?.readUrl,
			});
			return presetId && stickerPackId && thumbnailUrl
				? ({
						key: stickerPackId,
						shape: "circle",
						optionItem: {
							stickerPack: {
								presetId,
								stickerPackId,
								thumbnailUrl,
							},
						},
					} satisfies ThemeCategoryOption)
				: null;
		})
		.filter(Boolean) as ThemeCategoryOption[];

	const reactionsFromStickerPacks = suggestions
		.map(({ reaction }) => reaction?.emoji)
		.filter(Boolean) as string[];
	const uniqueReactions = new Set([...EMOJI_REACTIONS, ...reactionsFromStickerPacks]);
	const reactionOptions = Array.from(uniqueReactions).map((emoji) => ({
		key: emoji,
		shape: "circle",
		optionItem: { reaction: emoji },
	})) satisfies ThemeCategoryOption[];

	return {
		templateOptions,
		stickerOptions,
		paletteOptions,
		reactionOptions,
	};
};

export default parseOptionsFromThemeSuggestions;
